import { clsx } from 'clsx';
import { cloneElement } from 'react';

import type { ColorKey } from '../../theme/themeColors.types';
import { marginCSSVars } from '../../utils/spacing';
import { Flex } from '../flex/flex';
import { DismissIcon } from '../icon/icons/DismissIcon';
import { Typography } from '../typography/typography';
import type { BannerProps } from './banner.types';

import styles from './banner.module.css';

const COLOR_MAP: { [key: string]: ColorKey } = {
  error: 'error-dark',
  info: 'grays-black',
  success: 'success-dark',
};

const ICON_COLOR_MAP: { [key: string]: ColorKey } = {
  error: 'error',
  info: 'grays-black',
  success: 'success',
};

/**
 * Banner displays a short, important message with different variants depending on the type of alert.
 */

export function Banner({
  actions,
  className,
  'data-testid': testId,
  full = false,
  icon,
  id,
  label,
  onDismiss,
  style,
  typography,
  variant = 'info',
  ...marginProps
}: BannerProps) {
  const isText = typeof label === 'string';

  return (
    <div
      className={clsx(
        className,
        styles['banner'],
        styles[`banner__${variant}`],
        {
          [styles['banner__full']]: full,
        }
      )}
      data-testid={testId}
      id={id}
      style={{ ...style, ...marginCSSVars(marginProps) }}
    >
      <div className={styles['banner_content']}>
        {icon && (
          <Flex alignItems="center">
            {cloneElement(icon, {
              ...icon.props,
              mr: 12,
              color: ICON_COLOR_MAP[variant],
            })}
          </Flex>
        )}
        <Typography
          as={isText ? 'p' : 'div'}
          color={COLOR_MAP[variant]}
          variant={typography || 'body-semibold'}
        >
          {label}
        </Typography>
      </div>
      {actions}
      {onDismiss && (
        <div>
          <DismissIcon
            ml={16}
            onClick={onDismiss}
            color="grays-black"
            altText="dismiss banner"
          />
        </div>
      )}
    </div>
  );
}
